import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { Error404Component } from './shared/error-404.component';
import { Error500Component } from './shared/error-500.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [
    // Redirect empty path to '/example'
    {
        path: '',
        pathMatch: 'full',
        redirectTo: 'dashboard',
    },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    // { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'dashboard' },

    // | Auth routes for guests
    {
        path: 'auth',
        component: LayoutComponent,
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        data: {
            layout: 'empty',
        },
        children: [
            {
                path: 'reset-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/forgot-password/forgot-password.module'
                    ).then((m) => m.AuthForgotPasswordModule),
                data: {title: 'Forgot Password'}
            },
            {
                path: 'signin',
                loadChildren: () =>
                    import('app/modules/auth/sign-in/sign-in.module').then(
                        (m) => m.AuthSignInModule
                    ),
                data: {title: 'Signin'}
            },
            {
                path: 'signup',
                loadChildren: () =>
                    import('app/modules/auth/sign-up/sign-up.module').then(
                        (m) => m.AuthSignUpModule
                    ),
                data: {title: 'Signup'}
            },
            {
                path: 'set-password',
                loadChildren: () =>
                    import(
                        'app/modules/auth/set-password/set-password.module'
                    ).then((m) => m.SetPasswordModule),
                data: {title: 'Set Password'}
            },
            { path: '**', redirectTo: '/auth/signin' }
        ],
    },

    // | Admin routes
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        children: [
            {
                path: 'dashboard',
                data: { preload: true, delay: false, title: 'Dashboard' },
                loadChildren: () =>
                    import('app/modules/admin/dashboard/dashboard.module').then(
                        (m) => m.DashboardModule
                    ),
            },
            {
                path: 'manage',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/manage/manage.module'
                            ).then((m) => m.ManageModule),
                    },
                ],
            },
            {
                path: 'account',
                children: [
                    { path: '', redirectTo: 'settings', pathMatch: 'full' },
                    {
                        path: 'settings',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/settings/settings.module'
                            ).then((m) => m.SettingsModule),
                    },
                ],
            },
            {
                path: 'campaign',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/campaigns/campaigns.module'
                            ).then((m) => m.CampaignsModule),
                    },
                ],
            },
            {
                path: 'prospect-management',
                loadChildren: () =>
                    import(
                        'app/modules/admin/lead-management/lead-management.module'
                    ).then((m) => m.LeadManagementModule),
                data: { title: 'Prospect Management' },
            },
            {
                path: 'prospect-finder',
                loadChildren: () =>
                    import(
                        'app/modules/admin/prospect-filter/prospect-filter.module'
                    ).then((m) => m.ProspectFilterModule),
                data: { title: 'Prospect Finder' },
            },
            {
                path: 'unified-inbox',
                loadChildren: () =>
                    import('app/modules/admin/inboxes/inboxes.module').then(
                        (m) => m.InboxesModule
                    ),
                data: { title: 'Unified Inbox' },
            },
            {
                path: 'email-accounts',
                loadChildren: () =>
                    import(
                        'app/modules/admin/email-accounts/email-accounts.module'
                    ).then((m) => m.EmailAccountsModule),
                data: { title: 'Email Accounts' },
            },
            {
                path: 'warmify',
                loadChildren: () =>
                    import('app/modules/admin/email-warm-up/email-warm-up.module').then(
                        (m) => m.EmailWarmUpModule
                    ),
                data: { title: 'Warmify' },
            },
            {
                path: 'templates',
                loadChildren: () =>
                    import('app/modules/admin/template/template.module').then(
                        (m) => m.TemplateModule
                    ),
                data: { title: 'Templates' },
            },
            {
                path: 'integrations',
                loadChildren: () =>
                    import('app/modules/admin/integrations/integrations.module').then(
                        (m) => m.IntegrationsModule
                    ),
                data: { title: 'Integrations' },
            },
            {
                path: 'onboarding',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/new-onboarding/new-onboarding.module'
                            ).then((m) => m.NewOnboardingModule),
                    },
                ],
            },
            {
                path: 'campaign-builder',
                children: [
                    {
                        path: '',
                        loadChildren: () =>
                            import(
                                'app/modules/admin/campaign/campaign.module'
                            ).then((m) => m.CampaignModule),
                    },
                ],
            },
        ],
    },
    { path: '404', component: Error404Component },
    { path: '500', component: Error500Component },
    { path: '**', redirectTo: '/dashboard'},
];
